import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  MountainSnow,
  Route,
  TrendingUp,
  Sun,
  Activity,
  Flame,
  CalendarDays,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { fetchProfileStats, fetchActivityHeatmap } from "../api/profile";
import { HeatGrid, buildHeatData } from "../components/HeatGrid";
import { ScoreMeter } from "../components/ScoreMeter";
import { StatBlock } from "../components/StatBlock";
import { AtlasPanel } from "../components/AtlasPanel";
import { Skel } from "../components/Skeleton";
import { ProGate } from "../components/ProGate";
import { InsightsSection } from "../components/InsightsSection";
import { useIsPro } from "../hooks/useIsPro";
import { useHistoryGate } from "../hooks/useHistoryGate";
import { isWrappedSeasonOpen, featuredWrappedYear } from "../lib/wrapped-season";

// ─── StatisticsPage ───────────────────────────────────────────────────────────

export default function StatisticsPage() {
  const statsQuery = useQuery({
    queryKey: ["profile/stats"],
    queryFn: fetchProfileStats,
  });
  const { isPro } = useIsPro();
  const { fullHistory } = useHistoryGate();
  // Older heatmap years open with Full history or Pro (spec 30). heatmapYear
  // is only ever set when yearsUnlocked; others selecting the history option
  // flip heatmapLocked instead (no fetch - the API would 402).
  const yearsUnlocked = isPro || fullHistory;
  const [heatmapYear, setHeatmapYear] = React.useState<number | null>(null);
  const [heatmapLocked, setHeatmapLocked] = React.useState(false);
  const heatmapQuery = useQuery({
    queryKey: ["profile/activity-heatmap", heatmapYear],
    queryFn: () => fetchActivityHeatmap(heatmapYear),
  });

  const stats = statsQuery.data?.stats;
  const streaks = statsQuery.data?.streaks;
  const heatData = heatmapQuery.data
    ? buildHeatData(
        heatmapQuery.data.data,
        52,
        7,
        heatmapYear != null ? new Date(heatmapYear, 11, 31) : undefined,
      )
    : null;
  const heatmapYears = heatmapQuery.data?.years ?? [];

  const statRow1 = [
    { label: "Summits", value: stats ? String(stats.totalSummits) : null, icon: <MountainSnow size={12} /> },
    {
      label: "Distance",
      value: stats ? `${(stats.totalDistanceM / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} km` : null,
      icon: <Route size={12} />,
    },
    {
      label: "Elevation gain",
      value: stats ? `${(stats.totalElevationGainM / 1000).toFixed(1)} km` : null,
      icon: <TrendingUp size={12} />,
    },
    { label: "Days out", value: stats ? String(stats.totalDaysOut) : null, icon: <Sun size={12} /> },
  ];

  const statRow2 = [
    { label: "Activities", value: stats ? String(stats.totalActivities) : null, icon: <Activity size={12} /> },
    {
      label: "Average outing",
      value:
        stats && stats.totalActivities > 0
          ? `${(stats.totalDistanceM / stats.totalActivities / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} km`
          : stats
            ? "0 km"
            : null,
      icon: <Route size={12} />,
    },
    { label: "Current streak", value: streaks ? `${streaks.currentStreak}d` : null, icon: <Flame size={12} /> },
    { label: "Longest streak", value: streaks ? `${streaks.longestStreak}d` : null, icon: <CalendarDays size={12} /> },
  ];

  return (
    <div
      style={{
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        className="atlas-topo r-page"
        style={{ position: "relative", padding: "28px 28px 24px" }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(0deg, var(--bg-app) 0%, color-mix(in srgb, var(--bg-app) 40%, transparent) 100%)",
          }}
        />
        <div style={{ position: "relative", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Your data</p>
            <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
              Statistics
            </h1>
          </div>
          {isWrappedSeasonOpen() && (
            <Link
              to="/wrapped/$year"
              params={{ year: String(featuredWrappedYear()) }}
              style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 16px",
                borderRadius: "var(--radius-pill)",
                background: "var(--accent-soft)",
                border: "1px solid var(--border-gold)",
                color: "var(--gold-300)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <Sparkles size={14} />
              Your {featuredWrappedYear()} Wrapped
            </Link>
          )}
        </div>
      </div>

      <div
        className="r-page-x"
        style={{
          padding: "0 28px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* ── Score meter ──────────────────────────────────────────────── */}
        {stats ? (
          <ScoreMeter
            score={stats.outdoorScore}
            level={stats.level}
            levelProgress={stats.levelProgress * 100}
            toNext={`${stats.toNextLevel.toLocaleString()} pts to level ${stats.level + 1}`}
          />
        ) : (
          <Skel height="88px" />
        )}

        {/* ── Stats row 1 ──────────────────────────────────────────────── */}
        <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {statRow1.map(({ label, value, icon }) => (
            <div
              key={label}
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-lg)",
                padding: 16,
                boxShadow: "var(--ring-top)",
              }}
            >
              {value != null ? (
                <StatBlock size="sm" label={label} value={value} icon={icon} />
              ) : (
                <Skel height="56px" />
              )}
            </div>
          ))}
        </div>

        {/* ── Stats row 2 ──────────────────────────────────────────────── */}
        <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {statRow2.map(({ label, value, icon }) => (
            <div
              key={label}
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-lg)",
                padding: 16,
                boxShadow: "var(--ring-top)",
              }}
            >
              {value != null ? (
                <StatBlock size="sm" label={label} value={value} icon={icon} />
              ) : (
                <Skel height="56px" />
              )}
            </div>
          ))}
        </div>

        {/* ── Insights (Pro) ───────────────────────────────────────────── */}
        <InsightsSection />

        {/* ── Activity heatmap ─────────────────────────────────────────── */}
        <AtlasPanel
          title="Activity history"
          action={
            <select
              aria-label="Heatmap range"
              value={heatmapLocked ? "history" : heatmapYear != null ? String(heatmapYear) : "past"}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "past") {
                  setHeatmapYear(null);
                  setHeatmapLocked(false);
                } else if (v === "history") {
                  setHeatmapLocked(true);
                } else {
                  setHeatmapYear(parseInt(v, 10));
                  setHeatmapLocked(false);
                }
              }}
              style={{
                background: "var(--surface-sunken)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-md)",
                color: "var(--text-secondary)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                padding: "4px 8px",
              }}
            >
              <option value="past">Past year</option>
              {yearsUnlocked ? (
                heatmapYears.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))
              ) : (
                <option value="history">All years (Full history)</option>
              )}
            </select>
          }
        >
          {heatmapLocked ? (
            <ProGate label="Older heatmap years come with Full history or Pro">
              <div className="r-scroll-x">
                <HeatGrid columns={52} rows={7} cell={11} gap={3} data={heatData} />
              </div>
            </ProGate>
          ) : (
            <div className="r-scroll-x">
              <HeatGrid columns={52} rows={7} cell={11} gap={3} data={heatData} />
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 14,
              justifyContent: "flex-end",
            }}
          >
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)" }}>Less</span>
            {["--heat-0", "--heat-1", "--heat-2", "--heat-3", "--heat-4"].map((h) => (
              <span
                key={h}
                style={{ width: 11, height: 11, borderRadius: 3, background: `var(${h})`, display: "block" }}
              />
            ))}
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)" }}>More</span>
          </div>
        </AtlasPanel>

        {/* Activities now have their own page (sidebar: Activities). */}
        <Link
          to="/activities"
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text-secondary)",
            textDecoration: "none",
          }}
        >
          View all activities <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
